import { Transaction } from '@stellar/stellar-sdk';
import * as React from 'react';
import { useEffect, useState } from 'react';

import { getVaultChallenge, sentToVault } from 'api/vault';

import type AccountService from 'services/account.service';
import { getVaultChallengeProgress } from 'services/vault-challenge';

import { ModalProps } from 'types/modal';

import Success from 'assets/icons/status/success.svg';
import Vault from 'assets/wallets/vault.svg';

import Button from 'basics/buttons/Button';
import { ModalWrapper } from 'basics/ModalAtoms';

import { Heading, Description, ErrorText } from './SignChallengeWithMultisig.styled';
import {
    ApprovalCard,
    VaultIcon,
    ApprovalText,
    SignatureProgress,
    Actions,
} from './SignChallengeWithVault.styled';

interface Props {
    tx: Transaction;
    account: AccountService;
}

const SignChallengeWithVault = ({ params, confirm }: ModalProps<Props>) => {
    const { tx, account } = params;
    const [error, setError] = useState('');
    const [attempt, setAttempt] = useState(0);
    const [signedXdr, setSignedXdr] = useState('');
    const initialProgress = () =>
        getVaultChallengeProgress(
            tx,
            { xdr: tx.toXDR(), network_passphrase: tx.networkPassphrase },
            account.signers,
            account.accountId(),
            account.thresholds.med_threshold,
        );
    const [progress, setProgress] = useState(initialProgress);

    useEffect(() => {
        const controller = new AbortController();
        let timer: ReturnType<typeof setTimeout>;
        setError('');
        setSignedXdr('');
        setProgress(initialProgress());
        const poll = async () => {
            try {
                if (Number(tx.timeBounds?.maxTime ?? 0) <= Date.now() / 1000) {
                    throw new Error(
                        'This request has expired. Close this window and connect again.',
                    );
                }
                const response = await getVaultChallenge(
                    Buffer.from(tx.hash()).toString('hex'),
                    controller.signal,
                );
                if (controller.signal.aborted) return;
                const nextProgress = getVaultChallengeProgress(
                    tx,
                    response,
                    account.signers,
                    account.accountId(),
                    account.thresholds.med_threshold,
                );
                setProgress(nextProgress);
                if (nextProgress.signed) {
                    setSignedXdr(nextProgress.signed.toXDR());
                    return;
                }
                timer = setTimeout(poll, 5000);
            } catch (err) {
                if (!controller.signal.aborted)
                    setError(
                        err instanceof Error
                            ? err.message
                            : 'Could not check Vault signatures. Please retry.',
                    );
            }
        };
        sentToVault(tx.toXDR(), controller.signal)
            .then(() => {
                if (!controller.signal.aborted) {
                    void poll();
                }
            })
            .catch(() => {
                if (!controller.signal.aborted)
                    setError('Could not send the request to LOBSTR Vault. Please retry.');
            });
        return () => {
            controller.abort();
            clearTimeout(timer);
        };
    }, [tx, account, attempt]);

    return (
        <ModalWrapper>
            <Heading>Verify your account</Heading>
            <Description>
                {progress.requiredCount !== null
                    ? `This account and ${progress.requiredCount} LOBSTR Vault co-signer${progress.requiredCount === 1 ? '' : 's'}`
                    : 'This account and LOBSTR Vault co-signers'}{' '}
                must approve this sign-in request to manage your notifications.
            </Description>
            <ApprovalCard>
                <VaultIcon>
                    <Vault viewBox="0 0 45 45" aria-hidden="true" />
                </VaultIcon>
                <ApprovalText>
                    <strong>Approve in LOBSTR Vault</strong>
                    <span>Open the Vault app and collect the required co-signer signatures.</span>
                    <SignatureProgress role="status" aria-live="polite">
                        {signedXdr && <Success viewBox="0 0 16 16" aria-hidden="true" />}
                        <span>
                            {progress.requiredCount !== null
                                ? `${progress.signedCount} of ${progress.requiredCount} co-signers signed`
                                : `${progress.signedCount} co-signer${progress.signedCount === 1 ? '' : 's'} signed${signedXdr ? ' · Ready to continue' : ' · Waiting for more approvals'}`}
                        </span>
                    </SignatureProgress>
                </ApprovalText>
            </ApprovalCard>
            {error && <ErrorText role="alert">{error}</ErrorText>}
            <Actions>
                {error ? (
                    <Button fullWidth onClick={() => setAttempt(value => value + 1)}>
                        Retry
                    </Button>
                ) : (
                    <Button
                        fullWidth
                        disabled={!signedXdr}
                        onClick={() => confirm({ xdr: signedXdr })}
                    >
                        Sign and continue
                    </Button>
                )}
            </Actions>
        </ModalWrapper>
    );
};

export default SignChallengeWithVault;
